import React, { createContext } from 'react'
import classnames from 'classnames'
import { Well } from '@pushly/aqe/lib/components'
import './org-dashboard.scss'

export type OrgDashboardSectionKey = 'headline' | 'domains' | 'content' | 'acquisition'

/** The id on a section's well, for links elsewhere on the page that scroll to it. */
export const sectionElementId = (sectionKey: OrgDashboardSectionKey): string => `org-dashboard-section-${sectionKey}`

/** The id on a section's heading, which takes focus when a link scrolls to the section. */
export const sectionHeadingElementId = (sectionKey: OrgDashboardSectionKey): string =>
    `org-dashboard-${sectionKey}-heading`

/** The id of the heading of the section a component sits in, for moving focus there when its own control goes away. */
export const SectionHeadingElementIdContext = createContext<string | undefined>(undefined)

type Props = {
    sectionKey: OrgDashboardSectionKey
    title: string
    /** No card of its own: the heading sits on the page and the content brings its own surface. */
    ghost?: boolean
    actions?: React.ReactNode
    children: React.ReactNode
}

/** A dashboard section: a heading that links elsewhere on the page can scroll to, and the content below it. */
export const SectionWell: React.FC<Props> = ({ sectionKey, title, ghost = false, actions, children }) => (
    <Well
        id={sectionElementId(sectionKey)}
        variant={ghost ? 'ghost' : undefined}
        padding={ghost ? 'none' : undefined}
        className={classnames('org-dashboard-section', { 'org-dashboard-section-ghost': ghost })}
        header={
            <h3 className="org-dashboard-section-heading" id={sectionHeadingElementId(sectionKey)} tabIndex={-1}>
                {title}
            </h3>
        }
        actions={actions}
    >
        <SectionHeadingElementIdContext.Provider value={sectionHeadingElementId(sectionKey)}>
            {children}
        </SectionHeadingElementIdContext.Provider>
    </Well>
)

type TableSectionWellProps = {
    sectionKey: OrgDashboardSectionKey
    children: React.ReactNode
}

/**
 * A section whose table brings its own header and surface (see DashboardTableCard). The well draws nothing itself.
 * It carries the id that links elsewhere on the page scroll to and the DataView class that the platform's DataView
 * stylesheet scopes its header and table rules to.
 */
export const TableSectionWell: React.FC<TableSectionWellProps> = ({ sectionKey, children }) => (
    <Well
        id={sectionElementId(sectionKey)}
        variant="ghost"
        padding="none"
        hideHeader
        className="org-dashboard-section org-dashboard-section-ghost org-dashboard-table-section data-view"
    >
        {children}
    </Well>
)
